import { create } from 'zustand'
import { logDev, isDev } from '@/utils/env'
import AuthorizationService from '@/services/AuthorizationService'
import PatientService from '@/services/PatientService'
import { RESPONSE } from '@/utils/constants'
import type { AuthStore, ApiError } from '@/types'

// The auth token lives in an HttpOnly cookie that JS cannot read, so we keep a
// small non-secret hint in localStorage ("am I logged in?") set on login and
// cleared on logout. On refresh we render optimistically from this hint so a
// logged-in user does not flash the login screen while the background `whoami`
// probe confirms the cookie is still valid.
const AUTH_HINT_KEY = 'tmc_authed'

const readAuthHint = (): boolean => {
  try {
    return localStorage.getItem(AUTH_HINT_KEY) === '1'
  } catch {
    return false
  }
}

const writeAuthHint = (value: boolean): void => {
  try {
    if (value) localStorage.setItem(AUTH_HINT_KEY, '1')
    else localStorage.removeItem(AUTH_HINT_KEY)
  } catch {
    // Private mode / storage disabled: the optimistic render just won't kick in.
  }
}

const NO_TEMP_CREDENTIALS = {
  tempEmail: null,
  tempPassword: null,
  tempToken: null,
  tempEmailCode: null,
} as const

// Wat er lokaal overblijft na uitloggen. logout() en logoutAllDevices() ruimen
// hetzelfde op, alleen de backend-call verschilt.
const LOGGED_OUT = {
  isAuthenticated: false,
  isLoading: false,
  isOnboarded: null,
  ...NO_TEMP_CREDENTIALS,
} as const

export const useAuthStore = create<AuthStore>((set) => ({
  // Render optimistically from the persisted hint; `bootstrap()` validates the
  // HttpOnly cookie in the background and logs out if it is no longer valid.
  isAuthenticated: readAuthHint(),
  isOnboarded: null,
  // Starts true: callers (root.tsx, _index.tsx, _protected/_layout.tsx) wait
  // for this to clear before deciding what to render, so a stale "logged out"
  // hint (e.g. this browser's first load after the hint was introduced) can't
  // bounce an actually-still-logged-in user to /login before bootstrap() has
  // had a chance to check the real cookie.
  isLoading: true,
  isDev,
  tempEmail: null,
  tempPassword: null,
  tempToken: null,
  tempEmailCode: null,

  // Validate the HttpOnly cookie in the background and reconcile the hint.
  bootstrap: async () => {
    set({ isLoading: true })
    try {
      await PatientService.whoami()
      writeAuthHint(true)
      set({ isAuthenticated: true, isLoading: false })
    } catch (err) {
      // Alleen een echte 401 zegt dat de cookie niet meer geldt.
      if ((err as ApiError)?.status === RESPONSE.RESP_UNAUTHORIZED) {
        writeAuthHint(false)
        set({ isAuthenticated: false, isLoading: false })
        return
      }
      set({ isLoading: false })
    }
  },

  // Called after a successful 2FA / registration: the backend already set the
  // cookie, so we only flip local state (no token to store).
  login: () => {
    writeAuthHint(true)
    set({ isAuthenticated: true, isLoading: false, ...NO_TEMP_CREDENTIALS })
  },

  logout: async () => {
    // Ask the backend to clear the HttpOnly cookie; JS cannot do it itself.
    try {
      await AuthorizationService.logout()
    } catch {
      // Even if the call fails (e.g. already-expired token) we still drop local state.
    }
    writeAuthHint(false)
    set(LOGGED_OUT)
  },

  logoutAllDevices: async () => {
    // Same local cleanup as logout(), but the backend call also revokes the
    // shared token itself -- every device is logged out, not just this one.
    try {
      await AuthorizationService.logoutAllDevices()
    } catch {
      // Even if the call fails we still drop local state.
    }
    writeAuthHint(false)
    set(LOGGED_OUT)
  },

  // null betekent "nog niet bekend", geen "niet onboarded".
  setOnboarding: (result: boolean | null) => {
    logDev('ONBOARDING RESULT', result)
    set({ isOnboarded: result })
  },

  setTempCredentials: (email: string, password: string, token: string | null, code?: string | null) => {
    set({ tempEmail: email, tempPassword: password, tempToken: token, tempEmailCode: code ?? null })
  },

  clearTempCredentials: () => {
    set({ ...NO_TEMP_CREDENTIALS })
  },
}))
